import {DbServerErrors} from "./db-server-errors";
import { HttpResponse } from "@angular/common/http";
import {Config} from "../../config/config";
import {TextUtils} from "../utils/text-utils";
import {sha256} from 'js-sha256';

export class DbResponse {

  private static loggedMissingTranslations = new Set<string>();

  public response: HttpResponse<any>;
  public success: boolean;
  public data: any;
  public message: string;
  public serverErrors: DbServerErrors;

  public static create(response: any): DbResponse {
    return DbResponse.createCorsErrorResponse(response) || DbResponse.createNoManagerErrorResponse(response) || DbResponse.createSuccessResponse(response) || DbResponse.createErrorResponse(response);
  }

  private static getTranslatedMessage(message: string): string {
    if (!message) {
      return message;
    }

    // Decode HTML entities (&lt; &gt; &amp; etc.)
    const textarea = document.createElement('textarea');
    textarea.innerHTML = message;
    const decodedMessage = textarea.value;

    // Strip <br> tags (textMap.json now has combined entries)
    const strippedMessage = decodedMessage.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ');

    // textMap.json is inconsistent - some entries use straight apostrophes, some use curly
    // Backend can send either type, so try all combinations

    // Try 1: Hash as-is (whatever quotes backend sent)
    let normalized = strippedMessage.trim().toLowerCase();
    let hash = sha256(normalized).substring(0, 12);
    let textKey = `text_${hash}`;
    let translatedText = TextUtils.getText(textKey);

    if (translatedText !== `TEXT_NOT_FOUND::${textKey}`) {
      return translatedText;
    }

    // Try 2: Convert curly → straight apostrophes
    const withStraightQuotes = strippedMessage
      .replace(/\u2019/g, "'")  // curly apostrophe (') to straight (')
      .replace(/\u201C/g, '"')  // curly opening quote (") to straight (")
      .replace(/\u201D/g, '"'); // curly closing quote (") to straight (")

    normalized = withStraightQuotes.trim().toLowerCase();
    hash = sha256(normalized).substring(0, 12);
    textKey = `text_${hash}`;
    translatedText = TextUtils.getText(textKey);

    if (translatedText !== `TEXT_NOT_FOUND::${textKey}`) {
      return translatedText;
    }

    // Try 3: Convert straight → curly apostrophes
    const withCurlyQuotes = strippedMessage
      .replace(/'/g, '\u2019')  // straight apostrophe to curly (')
      .replace(/"/g, '\u201C')  // straight double quote to curly opening (")
      .replace(/"/g, '\u201D'); // straight double quote to curly closing (")

    normalized = withCurlyQuotes.trim().toLowerCase();
    hash = sha256(normalized).substring(0, 12);
    textKey = `text_${hash}`;
    translatedText = TextUtils.getText(textKey);

    if (translatedText !== `TEXT_NOT_FOUND::${textKey}`) {
      return translatedText;
    }

    // Only log each unique missing translation once
    if (!DbResponse.loggedMissingTranslations.has(hash)) {
      DbResponse.loggedMissingTranslations.add(hash);
      // console.log('✗ Translation NOT found:', message.substring(0, 80), '| hash:', hash);
    }
    return message;
  }

    private static translateDataFields(data: any): any {
        if (!data || typeof data !== 'object') {
            return data;
        }

        if (Array.isArray(data)) {
            return data.map(item => DbResponse.translateDataFields(item));
        }

        const translated = {};
        for (const key in data) {
            if (data.hasOwnProperty(key)) {
                const value = data[key];

                // Translate specific string fields
                if ((key === 'title' || key === 'description' || key === 'label' || key === 'message' || key === 'subdescription'
                    || (key === 'name' && ['Standard rate', 'Reduced rate', 'Zero rate'].includes(value))
                    || key === 'status') && typeof value === 'string') {
                    translated[key] = DbResponse.getTranslatedMessage(value);
                } else if ((key === 'values' || key === 'options') && value && typeof value === 'object' && !Array.isArray(value)) {
                    // Special handling for "values"/"options" objects - translate string values
                    translated[key] = {};
                    for (const valueKey in value) {
                        if (value.hasOwnProperty(valueKey)) {
                            if (typeof value[valueKey] === 'string') {
                                translated[key][valueKey] = DbResponse.getTranslatedMessage(value[valueKey]);
                            } else {
                                translated[key][valueKey] = value[valueKey];
                            }
                        }
                    }
                } else if (value && typeof value === 'object') {
                    // Recursively translate nested objects
                    translated[key] = DbResponse.translateDataFields(value);
                } else {
                    translated[key] = value;
                }
            }
        }
        return translated;
    }

  private static createCorsErrorResponse(response: any): DbResponse {
    let dbResponse: DbResponse;

    if (response.status === 0) {
      // related to CORS
      dbResponse = new DbResponse();
      dbResponse.response = response;
      dbResponse.success = false;
      dbResponse.message = TextUtils.getText('text_somethingWentWrongDot');
    }

    return dbResponse;
  }

  private static createNoManagerErrorResponse(response: any): DbResponse {
    let dbResponse: DbResponse;

    if (response && response.url && response.url.indexOf(Config.managerEndpoint) !== -1 && response.error && response.error.code === 'rest_no_route') {
      dbResponse = new DbResponse();
      dbResponse.response = response;
      dbResponse.success = false;
      dbResponse.message = TextUtils.getText('text_noManager');
    }

    return dbResponse;
  }

  private static createSuccessResponse(response: any): DbResponse {
    let dbResponse: DbResponse;

    if (response.status < 400) {
      dbResponse = new DbResponse();
      dbResponse.response = response;
      dbResponse.success = true;
      const rawMessage = response.message || response.body.message || response.msg || response.body.msg;
      dbResponse.message = DbResponse.getTranslatedMessage(rawMessage);
      let data: any = response;
      while (data.body || data.data) {
        data = data.body || data.data;
      }
      // Translate title, description, message fields in response data
      dbResponse.data = DbResponse.translateDataFields(data);
    }

    return dbResponse;
  }

  private static createErrorResponse(response: any): DbResponse {
    let dbResponse: DbResponse;
    if (response.status >= 400) {
      const _message = (response.error && response.error.error && (response.error.error.message || response.error.error.msg)) || (response.error && (response.error.msg || response.error.message));
      const _serverErrors = (response.error && response.error.error) || response.error;
      dbResponse = new DbResponse();
      dbResponse.response = response;
      dbResponse.success = false;
      dbResponse.message = _message ? DbResponse.getTranslatedMessage(_message) : TextUtils.getText('text_somethingWentWrongDot');
      dbResponse.serverErrors = _serverErrors ? DbServerErrors.create(DbResponse.translateDataFields(_serverErrors)) : null;
    }

    return dbResponse;
  }

}
